import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { CountryService } from "../../services/country.service";
import { Helper } from "src/app/core/helpers/helper";
import {
  ConditionOperator,
  DatePickerTabs,
  DeviceTypeString,
  DriverType,
  DriverTypeString,
  Gender,
  MassNotificationEntities,
  PaymentGatewayString,
  PaymentModeStrings,
  PaymentTransactionStatusString,
  RateRange,
  UserType
} from "src/app/core/constants/constants";
import { VehicleService } from "../../services/vehicle.service";
import { AllBookingStatusStrings, BookingCreatedBy, BookingStatus, CityBookingCreatedByString, PriceMode, PriceModeString } from "src/app/core/constants/bookingConstant";
import { LanguageService } from "src/app/core/services/language.service";
import { CommonService } from "../../services/common.service";
import { BankTransactionStatusTypeString, CreditTransactionTypeString } from "src/app/core/constants/walletConstant";

interface Filter {
  key: string;
  label: string;
  value: any;
  list: any[];

  labelKey: string;
  isArray?: boolean;
  translate?: boolean;
  translateToCurrentLang?: boolean;
  condition?: any;
}

interface AppliedFilterDetails {
  key: string;
  label: string;
  value: any;
  valueCount: number;
  translate?: boolean;
  translateToCurrentLang?: boolean;
  isDate?: boolean;
}

interface CollapsibleFilter {
  title: string;
  placeholder: string;
  isDropdown: boolean;
  isDatePicker: boolean;
  isInput: boolean;
  selectedValue: any;
  selectedOperator: any;
  isMultiLanguageValue: boolean;
  isMultiple: boolean;
  data: any[];
  conditionOperators: any[];
  filterKey: string;
  conditionOperatorKey: string;
  showAppliedFilter?: boolean;
  metaData?: any;
}

@Component({
  selector: "app-filter-data-modal",
  templateUrl: "./filter-data-modal.component.html",
  styleUrl: "./filter-data-modal.component.scss",
  standalone: false
})
export class FilterDataModalComponent {
  datePlaceholder: string = "label-title.select-dates";
  selectedCountry: string = "all";
  selectedCity: string = "all";
  selectedType: string = "all";
  selectedTag: string = "all";
  selectedUserType: string | null = null;
  selectedDeviceType: string | null = null;
  selectedWalletTransactionType: string | null = null;
  selectedBankTransactionStatusType: any = null;
  selectedPaymentGateWayType: any = null;
  selectedWalleTransactionType: string | null = null;
  selectedBrand: string | null = null;
  selectedBrandModel: string | null = null;

  filterType: any;
  selectedStatus: any = null;
  @Input() activeTab: number = DatePickerTabs.day;

  formattedDate: any;
  filterData: any = {};

  todayDate: Date = new Date();

  allFilters: Filter[] = [];

  cityList: Array<any> = [];
  countryList: Array<any> = [];
  fieldsToFilter: Array<any> = [];
  vehicleTypesList: Array<any> = [];
  selectedBookingTag: Array<any> = [];
  statusTypeArray: Array<any> = [];
  paymentGateWayTypeArray: Array<any> = [];
  creditTransactionTypes: Array<any> = [];
  vehicleBrandList: Array<any> = [];
  vehicleBrandModelList: Array<any> = [];

  showDatePicker: boolean = false;
  isScheduleDate: boolean = false;

  isOpen: any = {
    timeSpan: false,
    recurrence: false,
    createdTime: false,
    completionDate: false
  };

  recurrenceOption: string = "is";
  isEnabled: boolean = false;
  isDisabled: boolean = false;

  collepsibleFilters: CollapsibleFilter[] = [];
  openSection: string | null = null;

  promoCodeList: Array<any> = [];
  accessibilityList: Array<any> = [];

  @Output() applyFilterEvent = new EventEmitter();
  @Input() filterDate: any = {};

  @ViewChild("modal", { static: true }) modal!: TemplateRef<any>;

  constructor(
    public offcanvasService: NgbOffcanvas,
    private countryService: CountryService,
    public helper: Helper,
    private vehicleService: VehicleService,
    public languageService: LanguageService,
    private commonService: CommonService
  ) {}

  /**
   * Opens the modal with the provided filter type, fields to filter, and filter data.
   * Fetches required filter data and initializes collapsible filters.
   *
   * @param filterType The type of filter to be applied.
   * @param fieldsToFilter The fields to be filtered.
   * @param filterData The data to be used for filtering.
   * @param datePlaceholder Placeholder text for the date picker.
   * @param isScheduleDate Indicates if the date is a scheduled date.
   */
  async openModal(filterType: any, fieldsToFilter: any, filterData: any = {}, datePlaceholder = this.datePlaceholder, isScheduleDate: boolean = false): Promise<void> {
    this.isScheduleDate = isScheduleDate;

    await this.fetchRequiredFilterData(fieldsToFilter);

    await this.getFilteredCityList(filterData);

    this.selectedCountry = this.getFilterValue(filterData.countryId, filterData.countryId, "all");
    this.selectedCity = this.getFilterValue(filterData.cityId, filterData.cityId, "all");

    this.fieldsToFilter = fieldsToFilter;

    this.initCollapsibleFilters();

    this.setFilterDetails(filterType, fieldsToFilter, filterData, datePlaceholder);

    this.setupStaticFilterData(fieldsToFilter);

    this.openFilterModal();
  }

  /**
   * Initializes the collapsible filters based on the fields to filter.
   */
  initCollapsibleFilters(): void {
    if (this.collepsibleFilters.length === 0) {
      this.addCollepsibleFilter(
        {
          title: "label-title.date",
          isDatePicker: true,
          showAppliedFilter: true,
          filterKey: "startDate",
          metaData: {
            activeTab: this.activeTab,
            isScheduleDate: this.isScheduleDate
          }
        },
        this.helper.FilterFields.DATE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.date",
          isDatePicker: true,
          selectedValue: "all",
          showAppliedFilter: true,
          filterKey: "startDate",
          conditionOperatorKey: "dateOperator",
          selectedOperator: "completedAt",
          conditionOperators: [
            { key: "createdAt", value: "label-title.created" },
            { key: "bookingTime", value: "label-title.booking-time" },
            { key: "completedAt", value: "label-title.completed" }
          ],
          metaData: {
            activeTab: this.activeTab,
            isScheduleDate: this.isScheduleDate
          }
        },
        this.helper.FilterFields.DATE_CUSTOM
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.country",
          isDropdown: true,
          showAppliedFilter: true,
          data: [{ key: "all", value: "All" }, ...this.countryList.map((item: any) => ({ key: item._id, value: item.name }))],
          filterKey: "countryId"
        },
        this.helper.FilterFields.COUNTRY
      );

      if (this.selectedCountry && this.selectedCountry !== "all") {
        this.addCollepsibleFilter(
          {
            title: "label-title.city",
            isDropdown: true,
            showAppliedFilter: true,
            data: [{ key: "all", value: "All" }, ...this.cityList.map((item: any) => ({ key: item._id, value: item.name }))],
            filterKey: "cityId"
          },
          this.helper.FilterFields.CITY
        );
      }

      this.addCollepsibleFilter(
        {
          title: "label-title.user-type",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.keys(MassNotificationEntities).map((key: any) => ({ key, value: MassNotificationEntities[key].string }))],
          filterKey: "userType"
        },
        this.helper.FilterFields.USER_TYPE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.device-type",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.entries(DeviceTypeString).map(([key, value]) => ({ key, value }))],
          filterKey: "deviceType"
        },
        this.helper.FilterFields.DEVICE_TYPE
      );

      /////////////////////////

      this.addCollepsibleFilter(
        {
          title: "label-title.description",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.entries(CreditTransactionTypeString).map(([key, value]) => ({ key, value }))],
          filterKey: "transactionType"
        },
        this.helper.FilterFields.WALLET_DESCRIPTION
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.description",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.entries(CreditTransactionTypeString).map(([key, value]) => ({ key, value }))],
          filterKey: "transactionType"
        },
        this.helper.FilterFields.REDEEM_DESCRIPTION
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.status",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.entries(BankTransactionStatusTypeString).map(([key, value]) => ({ key, value }))],
          filterKey: "status"
        },
        this.helper.FilterFields.BANK_TRANSACTION_STATUS
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.payment-gateway",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.entries(PaymentGatewayString).map(([key, value]) => ({ key, value }))],
          filterKey: "paymentGatewayType"
        },
        this.helper.FilterFields.PAYMENTGATEWAY
      );

      /////////////////////////

      this.addCollepsibleFilter(
        {
          title: "label-title.driver-type",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.keys(DriverTypeString).map((key: any) => ({ key, value: DriverType[key] }))],
          filterKey: "tag"
        },
        this.helper.FilterFields.TAGS
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.vehicle-type",
          isDropdown: true,
          isMultiLanguageValue: true,
          showAppliedFilter: true,
          data: [{ key: "all", value: "All" }, ...this.vehicleTypesList.map((item: any) => ({ key: item._id, value: item.name }))],
          filterKey: "vehicleTypeId"
        },
        this.helper.FilterFields.TYPE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.booking-tag",
          isDropdown: true,
          isMultiple: true,
          showAppliedFilter: true,
          data: this.helper.BookingHistoryFilterBookingType.map((item: any) => ({ key: item.key, value: item.string })),
          filterKey: "bookingTags",
          metaData: {
            isTypeList: false
          }
        },
        this.helper.FilterFields.BOOKING_TAG
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.status",
          isDropdown: true,
          showAppliedFilter: true,
          data: [
            { key: "all", value: "All" },
            { key: BookingStatus.REQUESTED, value: AllBookingStatusStrings[BookingStatus.REQUESTED] },
            { key: BookingStatus.ACCEPTED, value: AllBookingStatusStrings[BookingStatus.ACCEPTED] },
            { key: BookingStatus.ARRIVED_AT_PICKUP, value: AllBookingStatusStrings[BookingStatus.ARRIVED_AT_PICKUP] },
            { key: BookingStatus.STARTED, value: AllBookingStatusStrings[BookingStatus.STARTED] },
            { key: BookingStatus.ARRIVED_AT_STOP, value: AllBookingStatusStrings[BookingStatus.ARRIVED_AT_STOP] }
          ],
          filterKey: "status"
        },
        this.helper.FilterFields.BOOKING_STATUS
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.accessibility",
          isDropdown: true,
          isMultiple: true,
          isMultiLanguageValue: true,
          data: this.accessibilityList.map((item: any) => ({ key: item._id, value: item.accessibility })),
          filterKey: "accessibilityIds"
        },
        this.helper.FilterFields.ACCESSIBILITY
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.gender",
          isDropdown: true,
          data: [{ key: "all", value: "label-title.any" }, ...Object.keys(Gender).map((key: any) => ({ key, value: `label-title.${key.replace(/_/g, "-").toLocaleLowerCase()}` }))],
          filterKey: "gender"
        },
        this.helper.FilterFields.GENDER
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.language",
          isDropdown: true,
          isMultiple: true,
          data: [...this.languageService.supportedLanguages.map((language: any) => ({ key: language.code, value: language.label }))],
          filterKey: "speakingLanguages"
        },
        this.helper.FilterFields.LANGUAGE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.book-for",
          isDropdown: true,
          data: [
            { key: "all", value: "label-title.both" },
            { key: "false", value: "label-title.self" },
            { key: "true", value: "label-title.other" }
          ],
          filterKey: "isBookForOther"
        },
        this.helper.FilterFields.BOOK_FOR_OTHER
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.paused",
          isDropdown: true,
          showAppliedFilter: true,
          data: [
            { key: "all", value: "label-title.both" },
            { key: "true", value: "label-title.yes" },
            { key: "false", value: "label-title.no" }
          ],
          filterKey: "isPaused",
          metaData: {
            isTypeList: false
          }
        },
        this.helper.FilterFields.PAUSED
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.promo-status",
          isDropdown: true,
          data: [
            { key: "all", value: "label-title.both" },
            { key: "true", value: "label-title.applied" },
            { key: "false", value: "label-title.not-applied" }
          ],
          filterKey: "isPromoApplied"
        },
        this.helper.FilterFields.PROMO_STATUS
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.promo",
          isDropdown: true,
          isMultiple: true,
          data: [...this.promoCodeList.map((item: any) => ({ key: item._id, value: item.code }))],
          filterKey: "promoIds"
        },
        this.helper.FilterFields.PROMO
      );

      this.addCollepsibleFilter(
        {
          title: "heading-title.price-type",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.keys(PriceModeString).map((key: any) => ({ key, value: PriceMode[key] }))],
          filterKey: "bookingType"
        },
        this.helper.FilterFields.PRICE_TYPE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.payment-mode",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.keys(PaymentModeStrings).map((key: any) => ({ key, value: PaymentModeStrings[key] }))],
          filterKey: "paymentMode"
        },
        this.helper.FilterFields.PAYMENT_MODE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.payment-status",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.keys(PaymentTransactionStatusString).map((key: any) => ({ key, value: PaymentTransactionStatusString[key] }))],
          filterKey: "paymentStatus"
        },
        this.helper.FilterFields.PAYMENT_STATUS
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.created-by",
          isDropdown: true,
          data: [{ key: "all", value: "All" }, ...Object.keys(CityBookingCreatedByString).map((key: any) => ({ key, value: BookingCreatedBy[key] }))],
          filterKey: "createdBy"
        },
        this.helper.FilterFields.CREATED_BY
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.customer",
          isDropdown: true,
          isMultiple: true,
          filterKey: "customerIds",
          metaData: {
            isTypeList: true,
            type: UserType.CUSTOMER,
            placeholder: "label-title.search-by-name-email-phone"
          }
        },
        this.helper.FilterFields.CUSTOMERS
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.driver",
          isDropdown: true,
          isMultiple: true,
          filterKey: "driverIds",
          metaData: {
            isTypeList: true,
            type: UserType.DRIVER,
            placeholder: "label-title.search-by-name-email-phone"
          }
        },
        this.helper.FilterFields.DRIVER
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.partner",
          isDropdown: true,
          isMultiple: true,
          filterKey: "partnerIds",
          metaData: {
            isTypeList: true,
            type: UserType.PARTNER,
            placeholder: "label-title.search-by-name-email-phone"
          }
        },
        this.helper.FilterFields.PARTNER
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.corporate",
          isDropdown: true,
          isMultiple: true,
          filterKey: "corporateIds",
          metaData: {
            isTypeList: true,
            type: UserType.CORPORATE,
            placeholder: "label-title.search-by-name-email-phone"
          }
        },
        this.helper.FilterFields.CORPORATE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.hub",
          isDropdown: true,
          isMultiple: true,
          isMultiLanguageValue: true,
          filterKey: "hubIds",
          metaData: {
            isTypeList: true,
            type: UserType.HUB,
            placeholder: "label-title.search-by-name"
          }
        },
        this.helper.FilterFields.HUB
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.vehicle",
          isDropdown: true,
          isMultiple: true,
          filterKey: "vehicleIds",
          metaData: {
            isTypeList: true,
            type: UserType.VEHICLE,
            placeholder: "label-title.search-by-plate-no"
          }
        },
        this.helper.FilterFields.VEHICLE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.brand",
          isDropdown: true,
          isMultiLanguageValue: true,
          data: [{ key: "all", value: "All" }, ...this.vehicleBrandList.map((item: any) => ({ key: item._id, value: item.name }))],
          filterKey: "brandId"
        },
        this.helper.FilterFields.BRAND
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.model",
          isDropdown: true,
          isMultiple: true,
          isMultiLanguageValue: true,
          data: this.vehicleBrandModelList.map((item: any) => ({ key: item._id, value: item.name })),
          filterKey: "modelId"
        },
        this.helper.FilterFields.BRAND_MODEL
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.total",
          isInput: true,
          data: [{ key: "all", value: "label-title.any" }, ...RateRange.map((key: any) => ({ key, value: key }))],
          filterKey: "total",
          conditionOperatorKey: "totalOperator",
          selectedOperator: ConditionOperator.GREATER_THAN,
          conditionOperators: [
            ...[ConditionOperator.EQUAL, ConditionOperator.GREATER_THAN, ConditionOperator.LESS_THAN, ConditionOperator.LESS_THAN_EQUAL, ConditionOperator.GREATER_THAN_EQUAL].map(
              (key: any) => ({
                key,
                value: `label-title.${ConditionOperator[key].replace(/_/g, "-").toLocaleLowerCase()}`
              })
            )
          ]
        },
        this.helper.FilterFields.TOTAL
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.customer-rate",
          isDropdown: true,
          data: [{ key: "all", value: "label-title.any" }, ...RateRange.map((key: any) => ({ key, value: key }))],
          filterKey: "customerRate",
          conditionOperatorKey: "customerRateOperator",
          selectedOperator: ConditionOperator.GREATER_THAN,
          conditionOperators: [
            ...[ConditionOperator.EQUAL, ConditionOperator.GREATER_THAN, ConditionOperator.LESS_THAN, ConditionOperator.LESS_THAN_EQUAL, ConditionOperator.GREATER_THAN_EQUAL].map(
              (key: any) => ({
                key,
                value: `label-title.${ConditionOperator[key].replace(/_/g, "-").toLocaleLowerCase()}`
              })
            )
          ]
        },
        this.helper.FilterFields.RATE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.driver-rate",
          isDropdown: true,
          data: [{ key: "all", value: "label-title.any" }, ...RateRange.map((key: any) => ({ key, value: key }))],
          filterKey: "driverRate",
          conditionOperatorKey: "driverRateOperator",
          selectedOperator: ConditionOperator.GREATER_THAN,
          conditionOperators: [
            ...[ConditionOperator.EQUAL, ConditionOperator.GREATER_THAN, ConditionOperator.LESS_THAN, ConditionOperator.LESS_THAN_EQUAL, ConditionOperator.GREATER_THAN_EQUAL].map(
              (key: any) => ({
                key,
                value: `label-title.${ConditionOperator[key].replace(/_/g, "-").toLocaleLowerCase()}`
              })
            )
          ]
        },
        this.helper.FilterFields.RATE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.distance",
          isInput: true,
          data: [{ key: "all", value: "label-title.any" }, ...RateRange.map((key: any) => ({ key, value: key }))],
          filterKey: "distance",
          conditionOperatorKey: "distanceOperator",
          selectedOperator: ConditionOperator.GREATER_THAN,
          conditionOperators: [
            ...[ConditionOperator.EQUAL, ConditionOperator.GREATER_THAN, ConditionOperator.LESS_THAN, ConditionOperator.LESS_THAN_EQUAL, ConditionOperator.GREATER_THAN_EQUAL].map(
              (key: any) => ({
                key,
                value: `label-title.${ConditionOperator[key].replace(/_/g, "-").toLocaleLowerCase()}`
              })
            )
          ]
        },
        this.helper.FilterFields.DISTANCE
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.time",
          isInput: true,
          data: [{ key: "all", value: "label-title.any" }, ...RateRange.map((key: any) => ({ key, value: key }))],
          filterKey: "time",
          conditionOperatorKey: "timeOperator",
          selectedOperator: ConditionOperator.GREATER_THAN,
          conditionOperators: [
            ...[ConditionOperator.EQUAL, ConditionOperator.GREATER_THAN, ConditionOperator.LESS_THAN, ConditionOperator.LESS_THAN_EQUAL, ConditionOperator.GREATER_THAN_EQUAL].map(
              (key: any) => ({
                key,
                value: `label-title.${ConditionOperator[key].replace(/_/g, "-").toLocaleLowerCase()}`
              })
            )
          ]
        },
        this.helper.FilterFields.TIME
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.waiting-time",
          isInput: true,
          data: [{ key: "all", value: "label-title.any" }, ...RateRange.map((key: any) => ({ key, value: key }))],
          filterKey: "waitingTime",
          conditionOperatorKey: "waitingTimeOperator",
          selectedOperator: ConditionOperator.GREATER_THAN,
          conditionOperators: [
            ...[ConditionOperator.EQUAL, ConditionOperator.GREATER_THAN, ConditionOperator.LESS_THAN, ConditionOperator.LESS_THAN_EQUAL, ConditionOperator.GREATER_THAN_EQUAL].map(
              (key: any) => ({
                key,
                value: `label-title.${ConditionOperator[key].replace(/_/g, "-").toLocaleLowerCase()}`
              })
            )
          ]
        },
        this.helper.FilterFields.WAITING_TIME
      );

      this.addCollepsibleFilter(
        {
          title: "label-title.traffic-time",
          isInput: true,
          data: [{ key: "all", value: "label-title.any" }, ...RateRange.map((key: any) => ({ key, value: key }))],
          filterKey: "trafficTime",
          conditionOperatorKey: "trafficTimeOperator",
          selectedOperator: ConditionOperator.GREATER_THAN,
          conditionOperators: [
            ...[ConditionOperator.EQUAL, ConditionOperator.GREATER_THAN, ConditionOperator.LESS_THAN, ConditionOperator.LESS_THAN_EQUAL, ConditionOperator.GREATER_THAN_EQUAL].map(
              (key: any) => ({
                key,
                value: `label-title.${ConditionOperator[key].replace(/_/g, "-").toLocaleLowerCase()}`
              })
            )
          ]
        },
        this.helper.FilterFields.TRAFFIC_TIME
      );
    }
  }

  /**
   * Adds a collapsible filter to the list of filters.
   *
   * @param filter The filter object containing necessary information.
   * @param filterType The type of filter to be added.
   */
  addCollepsibleFilter(filter: Partial<CollapsibleFilter>, filterType: any): void {
    // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
    this.fieldsToFilter.forEach((field) => {
      const FilterField = Array.isArray(field) ? field[0] : field;
      const FilterKey = Array.isArray(field) ? field[1] : null;

      if (FilterField === filterType) {
        const filterData: CollapsibleFilter = {
          title: filter.title as string,
          placeholder: filter.placeholder ?? (filter.title as string),
          isDropdown: filter.isDropdown ?? false,
          isInput: filter.isInput ?? false,
          isDatePicker: filter.isDatePicker ?? false,
          selectedValue: this.getFilterSelectedValue(filter),
          selectedOperator: filter.selectedOperator ?? filter.conditionOperators?.[0].key,
          isMultiLanguageValue: filter.isMultiLanguageValue ?? false,
          isMultiple: filter.isMultiple ?? false,
          data: filter.data ?? [],
          conditionOperators: filter.conditionOperators ?? [],
          filterKey: FilterKey ?? (filter.filterKey as string),
          conditionOperatorKey: filter.conditionOperatorKey ?? "",
          showAppliedFilter: filter.showAppliedFilter ?? false,
          metaData: filter.metaData ?? { isTypeList: false }
        };

        if (filterType === this.helper.FilterFields.CITY) {
          const countryFilterIndex = this.collepsibleFilters.findIndex((x) => x.filterKey === "countryId");
          if (countryFilterIndex !== -1) {
            this.collepsibleFilters.splice(countryFilterIndex + 1, 0, filterData);
          } else {
            this.collepsibleFilters.push(filterData);
          }
        } else {
          this.collepsibleFilters.push(filterData);
        }
      }
    });
  }

  /**
   * Gets the selected value for a filter.
   *
   * @param filter The filter object containing necessary information.
   * @returns The selected value for the filter.
   */
  getFilterSelectedValue(filter: any): any {
    if (filter.isMultiple) {
      return filter.selectedValue ?? [];
    } else if (filter.isInput) {
      return filter.selectedValue ?? "";
    } else if (filter.isDatePicker) {
      return filter.selectedValue ?? null;
    }
    return filter.selectedValue ?? "all";
  }

  /**
   * Toggles the visibility of a section.
   *
   * @param sectionName The name of the section to be toggled.
   * @returns The updated state of the section.
   */
  toggleSection(sectionName: string): any {
    if (this.openSection === sectionName) {
      this.openSection = null; // Close the section if it's already open
    } else {
      this.openSection = sectionName; // Open the selected section and close others
    }
  }

  /**
   * Sets the filter details based on the provided filter type, fields to filter, and filter data.
   *
   * @param filterType The type of filter to be applied.
   * @param fieldsToFilter The fields to be filtered.
   * @param filterData The data to be used for filtering.
   * @param datePlaceholder Placeholder text for the date picker.
   */
  setFilterDetails(filterType: any, fieldsToFilter: any, filterData: any, datePlaceholder: any): void {
    this.datePlaceholder = datePlaceholder;
    this.filterData = filterData;
    this.filterType = filterType;

    this.selectedCountry = this.getFilterValue(filterData.countryId, filterData.countryId, "all");
    this.selectedCity = this.getFilterValue(filterData.cityId, filterData.cityId, "all");
    this.selectedType = filterData.vehicleTypeId ?? "all";
    this.selectedUserType = filterData.userType ?? null;
    this.selectedDeviceType = filterData.deviceType ?? null;
    this.selectedWalletTransactionType = filterData.transactionType ?? null;
    this.selectedPaymentGateWayType = filterData.paymentGatewayType ?? null;
    this.selectedBrand = filterData.brandId ?? null;
    this.selectedBrandModel = filterData.modelId ?? null;

    this.setBankTransactionStatus(fieldsToFilter, filterData);
    this.setUserTypeSpecificFilters(filterType, fieldsToFilter, filterData);
    this.setBookingHistorySpecificFilters(filterType, filterData);

    this.collepsibleFilters.forEach((filter: any) => {
      if (filter.isDropdown) {
        if (filterData[filter.filterKey]) {
          if (filter.metaData?.isTypeList) {
            filter.selectedValue = filterData[filter.filterKey].split(",") ?? [];
          } else if (filter.metaData?.conditionOperatorKey !== "") {
            filter.selectedOperator = filter.metaData?.selectedOperator;
          } else {
            filter.selectedValue = filterData[filter.filterKey] ?? "all";
          }
        }
        // }
      } else if (filter.isDatePicker && filterData[filter.filterKey]) {
        filter.selectedValue = filterData.formattedDate;
      }
    });
  }

  /**
   * Gets the filter value based on the provided ID and value.
   *
   * @param id The ID of the filter.
   * @param value The value of the filter.
   * @param defaultValue The default value to be used if the ID is not present.
   * @returns The filter value.
   */
  getFilterValue(id: any, value: any, defaultValue: any): any {
    return id ? value : defaultValue;
  }

  /**
   * Gets the filtered city list based on the provided filter data.
   *
   * @param filterData The data to be used for filtering.
   */
  async getFilteredCityList(filterData: any): Promise<void> {
    if (filterData.countryId && !filterData.cityId) {
      await this.countryChange(filterData.countryId);
    }
  }

  /**
   * Sets the bank transaction status based on the provided fields to filter and filter data.
   *
   * @param fieldsToFilter The fields to be filtered.
   * @param filterData The data to be used for filtering.
   */
  setBankTransactionStatus(fieldsToFilter: any, filterData: any): void {
    if (fieldsToFilter.includes(this.helper.FilterFields.BANK_TRANSACTION_STATUS)) {
      this.selectedBankTransactionStatusType = filterData.status ?? null;
    }
  }

  /**
   * Sets user type specific filters based on the provided filter type, fields to filter, and filter data.
   *
   * @param filterType The type of filter to be applied.
   * @param fieldsToFilter The fields to be filtered.
   * @param filterData The data to be used for filtering.
   */
  setUserTypeSpecificFilters(filterType: any, fieldsToFilter: any, filterData: any): void {
    if (filterType === this.helper.FilterType.USER && fieldsToFilter.includes(this.helper.FilterFields.TAGS)) {
      this.selectedTag = filterData.tag ?? "all";
    }
  }

  /**
   * Sets booking history specific filters based on the provided filter type and filter data.
   *
   * @param filterType The type of filter to be applied.
   * @param filterData The data to be used for filtering.
   */
  setBookingHistorySpecificFilters(filterType: any, filterData: any): void {
    if (filterType === this.helper.FilterType.BOOKING_HISTORY) {
      this.selectedStatus = filterData.status ?? null;

      if (filterData.bookingTags) {
        this.selectedBookingTag = filterData.bookingTags.split(",");
      }
    }
  }

  /**
   * Fetches the required filter data based on the provided fields to filter.
   *
   * @param fieldsToFilter The fields to be filtered.
   */
  async fetchRequiredFilterData(fieldsToFilter: any): Promise<void> {
    if (fieldsToFilter.includes(this.helper.FilterFields.COUNTRY) || fieldsToFilter.includes(this.helper.FilterFields.CITY)) {
      await this.getCountryCityList();
    }
    if (fieldsToFilter.includes(this.helper.FilterFields.TYPE)) {
      await this.getVehicleTypeList();
    }
    if (fieldsToFilter.includes(this.helper.FilterFields.BRAND)) {
      await this.getVehicleBrandList();
    }
    if (fieldsToFilter.includes(this.helper.FilterFields.ACCESSIBILITY)) {
      await this.getAccessibilityList();
    }
  }

  /**
   * Sets up static filter data based on the provided fields to filter.
   *
   * @param fieldsToFilter The fields to be filtered.
   */
  setupStaticFilterData(fieldsToFilter: any): void {
    if (fieldsToFilter.includes(this.helper.FilterFields.BANK_TRANSACTION_STATUS)) {
      this.statusTypeArray = Object.keys(this.helper.BankTransactionStatusTypeString).map((key) => ({
        key: key,
        string: this.helper.BankTransactionStatusTypeString[key]
      }));
    }
    if (fieldsToFilter.includes(this.helper.FilterFields.PAYMENTGATEWAY)) {
      this.paymentGateWayTypeArray = Object.keys(this.helper.PaymentGatewayString).map((key) => ({
        key: key,
        string: this.helper.PaymentGatewayString[key]
      }));
    }
  }

  /**
   * Opens the filter modal.
   */
  openFilterModal(): void {
    this.offcanvasService.open(this.modal, { position: "end", panelClass: "modal-right-lg", backdrop: "static" })?.result.then(
      () => {
        this.fieldsToFilter = [];
        this.clearFilter();
      },
      () => {
        this.fieldsToFilter = [];
        this.clearFilter();
      }
    );
  }

  /**
   * Gets the country and city list.
   *
   * @returns A promise that resolves with the country list.
   */
  getCountryCityList(): Promise<any> {
    return new Promise((resolve) => {
      if (this.countryList.length === 0) {
        this.countryService.getCountryCityList({}).subscribe((response: any) => {
          this.countryList = response?.countries ?? [];
          resolve(this.countryList);
        });
      } else {
        resolve(this.countryList);
      }
    });
  }

  /**
   * Handles the change event for the country filter.
   *
   * @param countryId The ID of the selected country.
   */
  countryChange(countryId: string): void {
    this.selectedCountry = countryId;
    this.cityList = [];
    // setTimeout(() => {
    if (this.selectedCountry !== "all") {
      const country = this.countryList.find((x) => x._id === countryId);
      this.cityList = country ? country.cities : [];
      this.selectedCity = "all";

      this.resetCityFilter();
    } else {
      this.resetCityFilter();
    }
    // }, 100);
  }

  /**
   * Resets the city filter.
   */
  resetCityFilter(): void {
    this.selectedCity = "all";
    delete this.filterData.cityId;
    const modelFilter = this.collepsibleFilters.find((x) => x.filterKey === "cityId");
    if (modelFilter) {
      modelFilter.data = [{ key: "all", value: "All" }, ...this.cityList.map((item: any) => ({ key: item._id, value: item.name }))];
      modelFilter.selectedValue = "all";
    } else if (this.fieldsToFilter.includes(this.helper.FilterFields.CITY)) {
      // Add city filter if not present below country filter

      this.addCollepsibleFilter(
        {
          title: "label-title.city",
          isDropdown: true,
          showAppliedFilter: true,
          data: [{ key: "all", value: "All" }, ...this.cityList.map((item: any) => ({ key: item._id, value: item.name }))],
          filterKey: "cityId"
        },
        this.helper.FilterFields.CITY
      );
    }
  }

  /**
   * Gets the vehicle type list.
   *
   * @returns A promise that resolves with the vehicle type list.
   */
  getVehicleTypeList(): Promise<any> {
    return new Promise((resolve) => {
      if (this.vehicleTypesList.length === 0) {
        const parameters = { isBusiness: true };
        this.vehicleService.getVehicleTypeList(parameters).subscribe((response: any) => {
          this.vehicleTypesList = response?.vehicleTypes ?? [];
          resolve(this.vehicleTypesList);
        });
      } else {
        resolve(this.vehicleTypesList);
      }
    });
  }

  /**
   * Gets the accessibility list.
   *
   * @returns A promise that resolves with the accessibility list.
   */
  getAccessibilityList(): Promise<any> {
    return new Promise((resolve) => {
      if (this.accessibilityList.length === 0) {
        this.commonService.getAccessibilityList().then((accessibilityList) => {
          this.accessibilityList = accessibilityList;
          resolve(this.accessibilityList);
        });
      } else {
        resolve(this.accessibilityList);
      }
    });
  }

  /**
   * Gets the vehicle brand list.
   *
   * @returns A promise that resolves with the vehicle brand list.
   */
  getVehicleBrandList(): Promise<any> {
    return new Promise((resolve) => {
      if (this.vehicleBrandList.length === 0) {
        this.vehicleService.getVehicleBrandList({}).subscribe((response: any) => {
          this.vehicleBrandList = response?.brands ?? [];
          resolve(this.vehicleBrandList);
        });
      } else {
        resolve(this.vehicleBrandList);
      }
    });
  }

  /**
   * Handles the change event for the brand filter.
   *
   * @param brandId The ID of the selected brand.
   */
  brandChange(brandId: string): void {
    this.selectedBrand = brandId;
    this.vehicleBrandModelList = [];

    if (this.fieldsToFilter.includes(this.helper.FilterFields.BRAND_MODEL) && this.selectedBrand) {
      this.getVehicleBrandModelList(brandId);
    }
  }

  /**
   * Gets the vehicle brand model list based on the provided brand ID.
   *
   * @param brandId The ID of the selected brand.
   * @returns A promise that resolves with the vehicle brand model list.
   */
  getVehicleBrandModelList(brandId: string): Promise<any> {
    return new Promise((resolve) => {
      if (this.vehicleBrandModelList.length === 0) {
        const parameters: any = { brandId: brandId, isShowErrorToast: false };
        this.vehicleService.getVehicleModelsList(parameters).subscribe((response: any) => {
          this.vehicleBrandModelList = response?.models ?? [];

          // Set the data for the model filter
          const modelFilter = this.collepsibleFilters.find((x) => x.filterKey === "vehicelModelIds" || x.filterKey === "modelId");
          if (modelFilter) {
            modelFilter.data = this.vehicleBrandModelList.map((item: any) => ({ key: item._id, value: item.name }));
            modelFilter.selectedValue = [];
          }
          resolve(this.vehicleBrandModelList);
        });
      } else {
        resolve(this.vehicleBrandModelList);
      }
    });
  }

  /**
   * Toggles the visibility of the date picker.
   */
  openDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
  }

  /**
   * Applies the selected filters and emits the applyFilterEvent.
   */
  applyFilter(): void {
    this.openSection = null;
    const filteredDetails: any = { ...this.filterData };
    const appliedFilterDetailsToShowInParent: AppliedFilterDetails[] = [];

    if (this.formattedDate) {
      filteredDetails["formattedDate"] = this.formattedDate;
    }

    this.setAppliedFilter(appliedFilterDetailsToShowInParent);
    this.applyFilterEvent.emit({ filteredDetails, appliedFilterDetailsToShowInParent });
    this.closeModal();
  }

  /**
   * Sets the applied filter details to be shown in the parent component.
   *
   * @param appliedFilterDetailsToShowInParent The array to store applied filter details.
   */
  setAppliedFilter(appliedFilterDetailsToShowInParent: any): void {
    this.collepsibleFilters.forEach((filter: any) => {
      if (filter.showAppliedFilter && filter.selectedValue !== "all") {
        if (Array.isArray(filter.selectedValue) && filter.selectedValue.length === 0) {
          return;
        }

        const filterObj: AppliedFilterDetails = {
          key: filter.filterKey,
          label: filter.title,
          value: this.getAppliedFilterValue(filter),
          valueCount: Array.isArray(filter.selectedValue) ? filter.selectedValue.length : 1,
          translate: true,
          translateToCurrentLang: filter.isMultiLanguageValue,
          isDate: filter.isDatePicker
        };

        if (filter.isDatePicker) {
          filterObj.value = { from: this.filterData.startDate, to: this.filterData.endDate };
        }
        appliedFilterDetailsToShowInParent.push(filterObj);
      }
    });
  }

  /**
   * Gets the applied filter value based on the provided filter.
   *
   * @param filter The filter object containing necessary information.
   * @returns The applied filter value.
   */
  getAppliedFilterValue(filter: any): string {
    const filteredData = filter.metaData.filteredData;
    if (!filteredData || filteredData.length === 0) {
      return filter.selectedValue;
    }

    const firstItem = filteredData[0];
    if (typeof firstItem === "object" && firstItem !== null) {
      return firstItem.label ?? firstItem.value;
    }

    return filteredData?.map((item: any) => item.label ?? item.value).join(", ") ?? filter.selectedValue;
  }

  /**
   * Adds a filter to the applied filters list.
   *
   * @param filter The filter object containing necessary information.
   * @param filteredDetails The object to store filtered details.
   * @param appliedFilterDetailsToShowInParent The array to store applied filter details.
   */
  addFilter(filter: Filter, filteredDetails: any, appliedFilterDetailsToShowInParent: AppliedFilterDetails[]): void {
    const { key, label, value, list, labelKey, isArray = false, translate = false, translateToCurrentLang = false } = filter;

    if (value && value !== "all" && (!isArray || value.length > 0)) {
      this.pushAppliedFilterDetails(filteredDetails, appliedFilterDetailsToShowInParent, list, key, label, value, labelKey, isArray, translate, translateToCurrentLang);
    } else {
      delete filteredDetails[key];
      const index = appliedFilterDetailsToShowInParent.findIndex((x) => x.key === key);
      if (index !== -1) appliedFilterDetailsToShowInParent.splice(index, 1);
    }
  }

  /**
   * Pushes the applied filter details to the list of applied filters.
   *
   * @param filteredDetails The object to store filtered details.
   * @param appliedFilterDetailsToShowInParent The array to store applied filter details.
   * @param list The list of filter options.
   * @param key The key of the filter.
   * @param label The label of the filter.
   * @param value The value of the filter.
   * @param labelKey The key to be used for the label.
   * @param isArray Indicates if the filter value is an array.
   * @param translate Indicates if the filter value should be translated.
   * @param translateToCurrentLang Indicates if the filter value should be translated to the current language.
   */
  // eslint-disable-next-line max-params
  pushAppliedFilterDetails(
    filteredDetails: any,
    appliedFilterDetailsToShowInParent: AppliedFilterDetails[],
    list: any[],
    key: string,
    label: string,
    value: any,
    labelKey: string,
    isArray = false,
    translate = false,
    translateToCurrentLang = false
  ): void {
    const processValue = (val: any): any => {
      const item = list.find((x: any) => x.key === val || x._id === val);
      return item ? item[labelKey] : val;
    };

    const labels = isArray ? value.map(processValue).join(", ") : processValue(value);
    filteredDetails[key] = isArray ? value.join(",") : value;

    appliedFilterDetailsToShowInParent.push({
      key,
      label,
      value: labels,
      valueCount: isArray ? value.length : 1,
      translate,
      translateToCurrentLang
    });
  }

  /**
   * Clears the selected filters and optionally applies the filter.
   *
   * @param applyFilter Indicates if the filter should be applied after clearing.
   */
  clearFilter(applyFilter: boolean = false): void {
    this.selectedCountry = "all";
    this.selectedCity = "all";
    this.selectedType = "all";
    this.selectedTag = "all";
    this.selectedUserType = null;
    this.selectedDeviceType = null;
    this.selectedStatus = null;
    this.selectedWalletTransactionType = null;
    this.selectedBankTransactionStatusType = null;
    this.selectedPaymentGateWayType = null;
    this.selectedBrand = null;
    this.selectedBrandModel = null;
    this.selectedBookingTag = [];
    this.formattedDate = null;
    this.filterDate = {};

    this.collepsibleFilters.forEach((filter: any) => {
      if (filter.isDropdown) {
        if (filter.isMultiple) {
          filter.selectedValue = [];
        } else {
          filter.selectedValue = "all";
        }
      } else if (filter.isInput) {
        filter.selectedValue = "";
      } else if (filter.isDatePicker) {
        filter.selectedValue = null;
      }
      // filter.selectedOperator = filter.conditionOperators?.[0]?.key;

      delete this.filterData[filter.filterKey];
    });
    delete this.filterData["startDate"];
    delete this.filterData["endDate"];

    setTimeout(() => {
      // this.filterData = {};

      if (applyFilter) {
        // this.setFilterDetails(filterType, fieldsToFilter, this.filterData, datePlaceholder);
        this.applyFilter();
      }
    }, 200);
  }

  /**
   * Closes the filter modal and clears the selected filters.
   */
  closeModal(): void {
    this.offcanvasService.dismiss(this.modal);
    this.showDatePicker = false;
    this.clearFilter();
  }

  /**
   * Handles the change event for the collapsible filters.
   *
   * @param event The event object containing filter data and configuration.
   */
  onCollapsibleFilterChange(event: any): void {
    // console.log(event);
    Object.entries(event.filterData).forEach(([key, value]) => {
      if (value !== "all") {
        if (Array.isArray(value)) {
          this.filterData[key] = value.join(",");
        } else {
          if (value) {
            this.filterData[key] = value;
          }
          if (event.filterConfig.conditionOperatorKey) {
            this.filterData[event.filterConfig.conditionOperatorKey] = event.filterConfig.selectedOperator;
          }
        }
      } else {
        delete this.filterData[key];
      }

      // eslint-disable-next-line sonarjs/cognitive-complexity
      this.collepsibleFilters.forEach((filter: any) => {
        if (filter.filterKey === key) {
          filter.metaData = { ...filter.metaData, ...event.filterConfig };
          if (filter.isDropdown) {
            if (filter.metaData?.isTypeList) {
              filter.data = event.filterConfig.filteredData;
              filter.selectedValue = value;
            } else {
              filter.selectedValue = value;
            }

            if (filter.metaData?.conditionOperatorKey !== "") {
              filter.selectedOperator = filter.metaData?.selectedOperator;
            }
          } else if (filter.isInput) {
            if (filter.metaData?.conditionOperatorKey !== "") {
              filter.selectedOperator = filter.metaData?.selectedOperator;
            }
            filter.selectedValue = value;
          } else if (filter.isDatePicker) {
            if (event.filterConfig.selectedValue) {
              this.formattedDate = event.filterConfig.selectedValue;
              filter.selectedValue = event.filterConfig.selectedValue;
            }
            if (filter.metaData?.conditionOperatorKey !== "") {
              filter.selectedOperator = filter.metaData?.selectedOperator;
            }
          }
        }
      });

      if (key === "vehicleBrandIds" || key === "brandId") {
        this.brandChange(value as string);
      } else if (key === "countryId") {
        this.countryChange(value as string);
      }
    });
  }
}
